import { useState, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import FoldableFileSection from '@/components/common/FoldableFileSection';
import GcodeByControllerRows from '@/components/common/GcodeByControllerRows';
import { isCadVectorFile, getFileIcon, getFileExtension } from '@/lib/fileType';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  IAttachmentCategory,
  IPostAttachment,
  IControllerType,
} from './post.d';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFileDownloader } from '@/lib/s3';
import {
  filterAttachments,
  filterAttachmentsByController,
  getAvailableControllers,
} from './post.utils';
import { AttachmentType } from '@/redux/attachment/attachment.d';
import { Toggle } from '@/components/ui/toggle';
import { CONTROLLER_CONFIG } from '@/lib/controllerConfig';
import { selectCurrentUser } from '../auth/auth.slice';
import { setShowLoginDialog } from '../global/global.slice';
import LiabilityWaiverDialog from '@/components/common/LiabilityWaiverDialog';
import HowToDownloadLink from '@/components/common/HowToDownloadLink';
import DownloadConsentNotice from '@/components/common/DownloadConsentNotice';
import { cn } from '@/lib/utils';

const LICENCE_URL_BY_TYPE: Record<string, string> = {
  'Creative Commons Public Domain':
    'https://creativecommons.org/publicdomain/zero/1.0/',
  'Creative Commons Attribution':
    'https://creativecommons.org/licenses/by/4.0/',
  'Creative Commons Attribution - Share Alike':
    'https://creativecommons.org/licenses/by-sa/4.0/',
  'Creative Commons Attribution - No Derivatives':
    'https://creativecommons.org/licenses/by-nd/4.0/',
  'Creative Commons Attribution - Noncommercial':
    'https://creativecommons.org/licenses/by-nc/4.0/',
  'Creative Commons Attribution - Noncommercial - Share Alike':
    'https://creativecommons.org/licenses/by-nc-sa/4.0/',
  'Creative Commons Attribution - Noncommercial - No Derivatives':
    'https://creativecommons.org/licenses/by-nc-nd/4.0/',
};

/** One file row in the download table. */
function AttachmentFileRow({
  attachment,
  muted,
  downloadingId,
  onDownload,
}: {
  attachment: IPostAttachment;
  muted: boolean;
  downloadingId: string | null;
  onDownload: (attachment: IPostAttachment) => void;
}) {
  return (
    <TableRow>
      <TableCell className="hidden sm:table-cell">
        {getFileIcon(attachment)}
      </TableCell>
      <TableCell className="py-2 overflow-hidden max-w-0">
        <p className="font-medium leading-none truncate text-sm">
          {attachment.fileName}
        </p>
      </TableCell>
      <TableCell className="text-center text-xs py-2">
        {getFileExtension(attachment.url)}
      </TableCell>
      <TableCell className="py-2">
        <div className="bg-secondary rounded-full">
          <Button
            size="sm"
            variant="default"
            className={cn(
              'w-full text-xs sm:text-sm py-2 px-3 h-auto',
              muted && 'opacity-60'
            )}
            onClick={() => onDownload(attachment)}
          >
            {downloadingId === attachment._id ? (
              <Loader2 className="h-3 w-3 animate-spin text-white" />
            ) : (
              'Download'
            )}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

const SECTION_CELL = 'font-bold text-sm py-2 px-0';

const CHIP_CLASS =
  'gap-2 border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary data-[state=off]:border-border h-8 px-3 text-sm';

function ControllerChips({
  controllers,
  selected,
  onSelect,
}: {
  controllers: IControllerType[];
  selected: IControllerType | 'ALL';
  onSelect: (controller: IControllerType | 'ALL') => void;
}) {
  if (controllers.length === 0) return null;

  return (
    <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0 justify-center sm:justify-start">
      <Toggle
        pressed={selected === 'ALL'}
        onPressedChange={() => onSelect('ALL')}
        className={CHIP_CLASS}
      >
        <span>All</span>
      </Toggle>
      {controllers.map((controllerType) => {
        const config = CONTROLLER_CONFIG[controllerType];
        const IconComponent = config.icon;
        return (
          <Toggle
            key={controllerType}
            pressed={selected === controllerType}
            onPressedChange={() => onSelect(controllerType)}
            className={CHIP_CLASS}
          >
            {IconComponent && <IconComponent className="w-3 h-3" />}
            <span>{config.name}</span>
          </Toggle>
        );
      })}
    </div>
  );
}

function DownloadAllButton({
  loading,
  muted,
  disabled,
  onClick,
}: {
  loading: boolean;
  muted: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      size="sm"
      onClick={onClick}
      className={cn(
        'rounded-full sm:ml-auto flex-shrink-0 w-full sm:w-auto',
        muted && 'opacity-60'
      )}
      disabled={disabled}
    >
      {loading ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin text-white" />
      ) : (
        'Download All'
      )}
    </Button>
  );
}

function LicenceNotice({
  licenceType,
  accepted,
  invalid,
  onAcceptedChange,
}: {
  licenceType?: string;
  accepted: boolean;
  invalid: boolean;
  onAcceptedChange: (next: boolean) => void;
}) {
  if (!licenceType) return null;
  const licenceUrl = LICENCE_URL_BY_TYPE[licenceType];

  return (
    <DownloadConsentNotice
      id="licence-accept"
      accepted={accepted}
      invalid={invalid}
      onAcceptedChange={onAcceptedChange}
    >
      {licenceUrl ? (
        <a
          href={licenceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium underline underline-offset-2 hover:text-primary"
          onClick={(e) => e.stopPropagation()}
        >
          {licenceType}
        </a>
      ) : (
        <span className="font-medium">{licenceType}</span>
      )}{' '}
      license <LiabilityWaiverDialog />.
    </DownloadConsentNotice>
  );
}

export default function DownloadModal({
  attachments,
  postTitle,
  licenceType,
  refetch,
  downloadCount,
}: {
  attachments: IPostAttachment[];
  postTitle: string;
  licenceType?: string;
  refetch: () => void;
  downloadCount: number;
}) {
  const [open, setOpen] = useState(false);
  const { downloadFile } = useFileDownloader();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [licenceAccepted, setLicenceAccepted] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [selectedController, setSelectedController] = useState<
    IControllerType | 'ALL'
  >('ALL');
  const downloadsDisabled = Boolean(licenceType) && !licenceAccepted;
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector(selectCurrentUser);

  // Get available controllers
  const availableControllers = useMemo(
    () => getAvailableControllers(attachments),
    [attachments]
  );
  const handleDownload = async (attachment: IPostAttachment) => {
    if (!isAuthenticated) {
      dispatch(setShowLoginDialog(true));
      return;
    }
    if (downloadsDisabled) {
      setNeedsConsent(true);
      return;
    }
    setDownloadingId(attachment._id);
    try {
      await downloadFile(attachment._id, attachment.fileName);
    } catch (error) {
      toast.error('Failed to download file, please try again later.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    if (!isAuthenticated) {
      dispatch(setShowLoginDialog(true));
      return;
    }
    if (downloadsDisabled) {
      setNeedsConsent(true);
      return;
    }
    const postAttachmentsZip = attachments.find((attachment) => {
      if (
        attachment.resourceType === AttachmentType.zip &&
        attachment.category === IAttachmentCategory.postZip
      ) {
        return true;
      }
      return false;
    });

    if (!postAttachmentsZip) {
      setDownloadingId('download_all');
      await refetch();
      toast.error('Download all is currently unavailable', {
        description: 'Please try again later.',
      });
      setDownloadingId(null);
      return;
    }
    setDownloadingId('download_all');
    try {
      await downloadFile(
        postAttachmentsZip._id,
        postTitle.replace(/\./g, '_').replace(/ /g, '_')
      );
    } catch (error) {
      toast.error('Failed to download files, please try again later.');
    } finally {
      setDownloadingId(null);
    }
  };

  // Apply controller filtering
  const controllerFilteredAttachments = useMemo(() => {
    const baseFiltered = filterAttachments(attachments);
    if (selectedController === 'ALL') {
      return baseFiltered;
    }
    return filterAttachmentsByController(baseFiltered, selectedController);
  }, [attachments, selectedController]);

  // Group attachments by type
  const groupedAttachments = useMemo(() => {
    const gcodeFiles = controllerFilteredAttachments.filter(
      (attachment) => attachment.resourceType === AttachmentType.model
    );

    // Pictures: true raster images (excluding SVG, AI, etc.)
    const pictureFiles = controllerFilteredAttachments.filter(
      (attachment) =>
        attachment.resourceType === AttachmentType.image &&
        !isCadVectorFile(attachment.fileName)
    );

    // CAD/Vectors: everything else (including SVG, AI, DXF, CRV, etc.)
    const cadFiles = controllerFilteredAttachments.filter(
      (attachment) =>
        attachment.resourceType !== AttachmentType.model &&
        (attachment.resourceType !== AttachmentType.image ||
          isCadVectorFile(attachment.fileName))
    );

    return { gcodeFiles, cadFiles, pictureFiles };
  }, [controllerFilteredAttachments]);

  const filteredAttachments = controllerFilteredAttachments;
  const renderRow = (attachment: IPostAttachment) => (
    <AttachmentFileRow
      key={attachment._id}
      attachment={attachment}
      muted={downloadsDisabled}
      downloadingId={downloadingId}
      onDownload={handleDownload}
    />
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="w-full rounded-full bg-secondary sm:w-auto">
          <Button
            variant="default"
            size="md"
            className="w-full gap-2 sm:w-auto"
          >
            Download
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black bg-opacity-50 text-xs text-white">
              {downloadCount}
            </span>
          </Button>
        </div>
      </DialogTrigger>
      <DialogContent className="w-[calc(100vw-8px)] max-w-[800px] max-h-[85vh] p-4 sm:p-6 flex flex-col gap-0">
        <DialogHeader className="pb-3">
          {/* pr-8 clears the dialog's own close button. */}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 pr-8">
            <DialogTitle className="text-lg sm:text-2xl font-primary text-left">
              Post Attachments
            </DialogTitle>
            <HowToDownloadLink />
          </div>
        </DialogHeader>
        <div className="flex flex-col flex-1 min-h-0 gap-3">
          {/* Controller Filter Chips and Download All Button */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-shrink-0">
            <ControllerChips
              controllers={availableControllers}
              selected={selectedController}
              onSelect={setSelectedController}
            />
            <DownloadAllButton
              loading={downloadingId === 'download_all'}
              muted={downloadsDisabled}
              disabled={attachments.length === 0}
              onClick={handleDownloadAll}
            />
          </div>
          <LicenceNotice
            licenceType={licenceType}
            accepted={licenceAccepted}
            invalid={needsConsent}
            onAcceptedChange={(next) => {
              setLicenceAccepted(next);
              if (next) setNeedsConsent(false);
            }}
          />
          <div className="flex-1 min-h-0 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="hidden sm:table-cell w-[50px]">
                    Type
                  </TableHead>
                  <TableHead>File Name</TableHead>
                  <TableHead className="w-[60px] text-center">Format</TableHead>
                  <TableHead className="w-[90px] text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAttachments.length > 0 ? (
                  <>
                    <GcodeByControllerRows
                      files={groupedAttachments.gcodeFiles}
                      cellClassName={SECTION_CELL}
                      renderRow={renderRow}
                    />
                    <FoldableFileSection
                      label="CAD / Vectors"
                      files={groupedAttachments.cadFiles}
                      defaultOpen={false}
                      cellClassName={SECTION_CELL}
                      renderRow={renderRow}
                    />
                    <FoldableFileSection
                      label="Pictures"
                      files={groupedAttachments.pictureFiles}
                      defaultOpen={false}
                      cellClassName={SECTION_CELL}
                      renderRow={renderRow}
                    />
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      No attachments available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
